import React, { useEffect } from 'react';

interface ConfirmDialogProps {
    open: boolean;
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export default function ConfirmDialog({
    open,
    title = 'Confirm',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
    onCancel,
}: ConfirmDialogProps) {
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onCancel]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
            <div className="relative w-full max-w-sm mx-auto bg-gray-900 border border-gray-700 shadow-2xl p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    {destructive && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold">!</span>}
                    {title}
                </h2>
                <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line mb-6">{message}</p>
                <div className="flex flex-col sm:flex-row-reverse gap-2">
                    <button
                        onClick={onConfirm}
                        className={`w-full sm:w-auto px-4 py-2 rounded-md font-medium transition-colors border text-sm ${destructive ? 'bg-red-600 hover:bg-red-700 border-red-500 text-white' : 'bg-blue-600 hover:bg-blue-700 border-blue-500 text-white'}`}
                    >
                        {confirmLabel}
                    </button>
                    <button
                        onClick={onCancel}
                        className="w-full sm:w-auto px-4 py-2 rounded-md font-medium bg-gray-800 hover:bg-gray-700 border border-gray-600 text-gray-200 text-sm"
                    >
                        {cancelLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
